"use client";

import "@/app/(public)/shared/tokens/colors.css";
import "@/app/(public)/shared/tokens/typography.css";
import { Button } from "@/app/(public)/shared/ui/Button";
import ScrollingLogos from "@/components/ui/landing/ScrollingLogos";
import { StringInput } from "@/components/ui/shared/SpecialInput";
import { vendorSignup } from "@/lib/database/client/database";
import { getCurrentUser } from "@/lib/db/users";
import { createClient } from "@/utils/supabase/client";
import { motion } from "motion/react";
import { useEffect, useState } from "react";

export const VendorSignupForm = () => {
  const supabase = createClient();

  const [userValid, setUserValid] = useState(false);
  const [fullName, setFullName] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [website, setWebsite] = useState("");
  const [workEmail, setWorkEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [description, setDescription] = useState("");

  const [showConfirmation, setShowConfirmation] = useState(false);

  const [fullNameTouched, setFullNameTouched] = useState(false);
  const [companyNameTouched, setCompanyNameTouched] = useState(false);
  const [websiteTouched, setWebsiteTouched] = useState(false);
  const [workEmailTouched, setWorkEmailTouched] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<"idle" | "success" | "error">("idle");
  const [submitMessage, setSubmitMessage] = useState("");

  const isValidEmail = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const handleFullNameChange = (value: string) => {
    setFullNameTouched(true);
    setFullName(value);
  };
  const handleCompanyNameChange = (value: string) => {
    setCompanyNameTouched(true);
    setCompanyName(value);
  };
  const handleWebsiteChange = (value: string) => {
    setWebsiteTouched(true);
    setWebsite(value);
  };
  const handleWorkEmailChange = (value: string) => {
    setWorkEmailTouched(true);
    setWorkEmail(value);
  };

  // Update submitForm function
  const submitForm = async () => {
    setFullNameTouched(true);
    setCompanyNameTouched(true);
    setWebsiteTouched(true);
    setWorkEmailTouched(true);
    setSubmitStatus("idle");
    setSubmitMessage("");

    if (!fullName || !companyName || !website || !workEmail || !isValidEmail(workEmail)) {
      setSubmitStatus("error");
      setSubmitMessage("Please fill in all required fields with valid information.");
      return;
    }

    setSubmitting(true);
    try {
      await vendorSignup(supabase, fullName, companyName, website, workEmail, phone, description);
      setFullName("");
      setWorkEmail("");
      setCompanyName("");
      setWebsite("");
      setPhone("");
      setDescription("");
      setShowConfirmation(true);
      setFullNameTouched(false);
      setCompanyNameTouched(false);
      setWebsiteTouched(false);
      setWorkEmailTouched(false);
    } catch (err) {
      setSubmitStatus("error");
      setSubmitMessage("Submission failed. Please try again later.");
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    (async () => {
      const user = await getCurrentUser(supabase);

      if (user) {
        setUserValid(true);
        setFullName(user.first_name + " " + user.last_name);
        setWorkEmail(user.email);
      }
    })();
  }, []);

  // Update useEffect to clear error
  useEffect(() => {
    if (
      submitStatus === "error" &&
      submitMessage === "Please fill in all required fields with valid information." &&
      fullName &&
      companyName &&
      website &&
      workEmail &&
      isValidEmail(workEmail)
    ) {
      setSubmitStatus("idle");
      setSubmitMessage("");
    }
  }, [fullName, companyName, website, workEmail, submitStatus, submitMessage]);

  return (
    <div>
      {/* Hero Section with Left-Right Layout */}
      <section className="container-top-form">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-16 h-full">
          {/* Left Content */}
          <div className="flex flex-col gap-8">
            <div className="flex flex-col gap-6">
              <h2 className="text-h2 text-landing-primary">Become a Partner with Nowadays</h2>
              <p className="text-body-lg text-landing-secondary max-w-530 mb-4">
                Apply within seconds! Complete our simple sign-up flow to become a Nowadays vendor.
              </p>

              {/* Scrolling Logos Section */}
              <div className="flex flex-col gap-4">
                <p className="text-body-lg text-landing-secondary italic font-bold">
                  Join other innovative companies like Nowadays.
                </p>
                <ScrollingLogos />
              </div>
            </div>
          </div>

          {/* Right Form */}
          <div className="flex items-start justify-center ">
            <motion.div
              className="w-full max-w-md"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
            >
              <div className="space-y-6">
                <div className="space-y-4">
                  <h3 className="text-h4 text-landing-primary">Vendor Information</h3>

                  <div>
                    <label className="text-body-sm font-medium text-landing-primary mb-2 block">Your Name*</label>
                    <StringInput
                      className="w-full input input-bordered bg-white border-landing-primary focus:border-landing-cta-light transition-colors"
                      placeholder="Ex: John Doe"
                      value={fullName}
                      setValue={handleFullNameChange}
                      validator={fullNameTouched ? fullName !== "" : true}
                    />
                    {fullNameTouched && !fullName && (
                      <div className="text-red-500 text-sm mt-1" role="alert">
                        Name is required.
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="text-body-sm font-medium text-landing-primary mb-2 block">Company Name*</label>
                    <StringInput
                      className="w-full input input-bordered bg-white border-landing-primary focus:border-landing-cta-light transition-colors"
                      placeholder="Ex: ABC Corp."
                      value={companyName}
                      setValue={handleCompanyNameChange}
                      validator={companyNameTouched ? companyName !== "" : true}
                    />
                    {companyNameTouched && !companyName && (
                      <div className="text-red-500 text-sm mt-1" role="alert">
                        Company name is required.
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="text-body-sm font-medium text-landing-primary mb-2 block">Website*</label>
                    <StringInput
                      className="w-full input input-bordered bg-white border-landing-primary focus:border-landing-cta-light transition-colors"
                      placeholder="Ex: www.abccorp.com"
                      value={website}
                      setValue={handleWebsiteChange}
                      validator={websiteTouched ? website !== "" : true}
                    />
                    {websiteTouched && !website && (
                      <div className="text-red-500 text-sm mt-1" role="alert">
                        Website is required.
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="text-body-sm font-medium text-landing-primary mb-2 block">Work Email*</label>
                    <StringInput
                      className="w-full input input-bordered bg-white border-landing-primary focus:border-landing-cta-light transition-colors"
                      placeholder="Ex: john@abccorp.com"
                      value={workEmail}
                      setValue={handleWorkEmailChange}
                      validator={workEmailTouched ? workEmail !== "" && isValidEmail(workEmail) : true}
                    />
                    {workEmailTouched && (!workEmail || !isValidEmail(workEmail)) && (
                      <div className="text-red-500 text-sm mt-1" role="alert">
                        {workEmail && !isValidEmail(workEmail)
                          ? "Please enter a valid email."
                          : "Work email is required."}
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="text-body-sm font-medium text-landing-primary mb-2 block">Phone</label>
                    <StringInput
                      className="w-full input input-bordered bg-white border-landing-primary focus:border-landing-cta-light transition-colors"
                      placeholder="Ex: 123-456-7890"
                      value={phone}
                      setValue={setPhone}
                      validator={true}
                    />
                  </div>

                  <div>
                    <label className="text-body-sm font-medium text-landing-primary mb-2 block">
                      Brief Description of services (Use 3rd person)!
                    </label>
                    <textarea
                      className="w-full h-24 input input-bordered bg-white border-landing-primary focus:border-landing-cta-light transition-colors resize-none"
                      placeholder=""
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                    />
                  </div>
                </div>

                {/* Submit Button */}
                <div className="pt-2">
                  {submitStatus !== "idle" && (
                    <div
                      className={`p-3 rounded-md text-sm ${
                        submitStatus === "success"
                          ? "bg-green-50 text-green-700 border border-green-200 mb-4"
                          : "bg-red-50 text-red-700 border border-red-200 mb-4"
                      }`}
                    >
                      {submitMessage}
                    </div>
                  )}
                  <Button variant="primary" size="md" onClick={submitForm} className="w-full" disabled={submitting}>
                    {submitting ? "Submitting..." : "Submit"}
                  </Button>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Confirmation Modal */}
      {showConfirmation && (
        <div
          className="fixed top-0 left-0 w-full h-full flex items-center justify-center bg-black bg-opacity-50 z-50"
          onClick={() => setShowConfirmation(false)}
        >
          <motion.div
            className="bg-white p-8 rounded-2xl max-w-md mx-4 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3 }}
          >
            <div className="text-center space-y-4">
              <div className="text-4xl">🎉</div>
              <h2 className="text-landing-primary text-h3">Thank You!</h2>
              <p className="text-body text-landing-secondary">
                Your vendor has been submitted for approval. You'll receive in email within 3 business days with any
                next steps!
              </p>
              <Button variant="primary" size="md" onClick={() => setShowConfirmation(false)}>
                Close
              </Button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};
